/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { PropsWithChildren } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, ButtonProps, IconButton, ListSubheader, Menu, MenuItem } from "@mui/material";
import { AccountCircle } from "@mui/icons-material";
import { useAppliedPropertyFeature } from "components/feature";
import { Feature, UserRole } from "types/gql-types";
import { Viewer } from "../auth/types";
import { ListsNavItem } from "./lists-nav-item";
import { MobileOpenProvider, useMobileOpen } from "./mobile-open-context";

export const headerButtonStyles = {
  padding: "3px 18px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  fontSize: "14px",
  fontWeight: "500",
  color: "white",
} as const;

const HeaderButton: React.FC<ButtonProps> = ({
  id,
  href,
  children,
  onClick,
  variant,
}) => (
  <Button
    id={id}
    onClick={onClick}
    size="small"
    color="inherit"
    href={href}
    style={{
      ...headerButtonStyles,
      color: variant === "outlined" ? "#37B4C9" : "white",
    }}
  >{children}</Button>
);

const ExternalLink: React.FC<PropsWithChildren<{href: string}>> = ({ href, children }) => {
  const { mobile: isMobile } = useMobileOpen();
  if (isMobile) {
    return (<a href={href} target="_blank" rel="noreferrer">
      <MenuItem>
        {children}
      </MenuItem>
    </a>);
  }
  return (
    <HeaderButton href={href}>{children}</HeaderButton>
  );
};

export const isLinkActive = (pathname: string, href: string, search?: string):boolean => {
  if (href === "/dash/manager/lists") {
    return ["/dash/manager/interest", href].some((url) => pathname.includes(url));
  }
  if (href.includes("?is-login=true")) return search === "?is-login=true";
  if (href.includes("?is-signup=true")) return search === "?is-signup=true";

  const [cleanHref] = href.split("?");
  return pathname.match(cleanHref) !== null;
};

export const AppLink: React.FC<PropsWithChildren<{href: string}>> = ({ href, children }) => {
  const location = useLocation();
  const { mobile: isMobile } = useMobileOpen();
  const shouldBeActive = isLinkActive(location.pathname, href, location.search);
  const key = href.split("/").pop();
  if (isMobile) {
    return (<Link to={href} id={`mobile-nav-${key}`}>
      <MenuItem selected={shouldBeActive}>
        {children}
      </MenuItem>
    </Link>);
  }

  return (
    <HeaderButton
      id={`header-nav-${key}`}
      variant={shouldBeActive ? "outlined" : "text"}
      href={href}
    >
      {children}
    </HeaderButton>
  );
};

const LoginLink: React.FC = () => {
  const isApplicationPath = window.location.pathname.startsWith("/application/");

  const href = isApplicationPath ? `${location.pathname}?is-login=true` : "/login";

  return <AppLink href={href}>Login</AppLink>;
};

const SignUpLink: React.FC = () => {
  const isApplicationPath = window.location.pathname.startsWith("/application/");

  const href = isApplicationPath ? `${location.pathname}?is-signup=true` : "/signup";

  return <AppLink href={href}>Sign Up</AppLink>;
};

const LinkList: React.FC<{ position?: "left" | "right", href: string, viewer: Viewer, mobile?: boolean, waitlist?: boolean, children: string }> =
({ children, ...props }) => (
  <ListsNavItem {...props}>
    {children}
  </ListsNavItem>
);

const ClientLinks = () => {
  const documentFeatureEnabled = useAppliedPropertyFeature(Feature.APPLICANT_DOCUMENT_UPLOAD);

  return (
    <>
      <AppLink href="/dash/client/submissions">Applications</AppLink>

      {documentFeatureEnabled && (<AppLink href="/dash/client/documents">Documents</AppLink>)}
    </>
  );
};

const PropertyManagerLinks: React.FC<{ viewer: Viewer, mobile?: boolean }> =
({ viewer, mobile }) => (
  <>
    <LinkList
      viewer={viewer}
      mobile={mobile}
      href="/dash/manager/lists"
    >Lists</LinkList>
    <LinkList
      waitlist
      position={mobile ? "right" : "left"}
      viewer={viewer}
      mobile={mobile}
      href="/dash/manager/update"
    >List Update</LinkList>
    <AppLink href="/dash/manager/messages">Failed Messages</AppLink>
    <AppLink href="/dash/manager/reports">Reports</AppLink>
    <AppLink href="/dash/manager/settings">Settings</AppLink>
  </>
);
export const ProfileButton: React.FC<{viewer?: Viewer | null, handleClose?: () => void}> =
// eslint-disable-next-line @typescript-eslint/no-empty-function
  ({ viewer, handleClose = () => {} }) => {
    const userProfileFeatureEnabled = useAppliedPropertyFeature(Feature.USER_PROFILE);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

    const handleMenuClose = () => {
      setAnchorEl(null);
      handleClose();
    };
    if (!userProfileFeatureEnabled) return null;
    if (!viewer) return null;

    return <>
      <IconButton
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <AccountCircle />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={open}
        onClose={handleMenuClose}
      >
        <ListSubheader>Logged in as {viewer.firstName} {viewer.lastName}</ListSubheader>
        <Link to="/dash/client/settings">
          <MenuItem onClick={handleMenuClose}>Your Profile</MenuItem>
        </Link>
        <Link to="/logout">
          <MenuItem onClick={handleMenuClose}>Logout</MenuItem>
        </Link>
      </Menu>
    </>;
  };

const Navigation: React.FC<{ viewer?: Viewer | null, mobile?: boolean, handleClose?: () => void }> =
// eslint-disable-next-line @typescript-eslint/no-empty-function
({ viewer, mobile = false, handleClose = () => {} }) => {
  const userProfileFeatureEnabled = useAppliedPropertyFeature(Feature.USER_PROFILE);

  const viewerHasRoles = (...roles: (UserRole | null)[]) => {
    if (!viewer && roles.includes(null)) return true;
    if (!viewer) return false;

    return viewer.roles.some(role => roles.includes(role as UserRole));
  };

  return (
    <>
      <MobileOpenProvider value={{ mobile, handleClose }}>
        {(!viewer || viewerHasRoles(UserRole.CLIENT)) && (
          <>
            <ExternalLink href="https://support.havenconnect.com/support/home">Support</ExternalLink>

            <AppLink href="/properties">Find Housing</AppLink>
          </>
        )}

        {viewerHasRoles(UserRole.CLIENT) && (<ClientLinks />)}
        {viewerHasRoles(UserRole.PROPERTY_MANAGER) && (
          <PropertyManagerLinks mobile={mobile} viewer={viewer!} />)}


        {!mobile && userProfileFeatureEnabled && <ProfileButton viewer={viewer}/>}

        {!userProfileFeatureEnabled && viewer && (<AppLink href="/logout">Logout</AppLink>)}
        {!viewer && (
          <>
            <LoginLink />
            <SignUpLink />
          </>)}
      </MobileOpenProvider>
    </>
  );
};

export default Navigation;
